import { IPublicClientApplication } from '@azure/msal-browser';
import { dvCreate, dvDelete, dvList, dvUpdate } from '../api/dataverseClient';
import { getToken, getAccount } from '../auth/authService';
import { graphGet } from '../api/graphClient';
import { CommentEntityType } from '../types';

// dagrun_pm_watcher.dagrun_pm_entitytype is a Picklist with the same
// integer mapping as Comment.entitytype — verified via
// EntityDefinitions on dagrun-pmis-prod. Reuse the existing enum so
// the UI / service code stays consistent across the comment + watcher
// + notification surfaces.
const WatcherEntityType = CommentEntityType;
type EntityTypeName = keyof typeof CommentEntityType;
function entityTypeToInt(t: string): number | null {
  return (WatcherEntityType as Record<string, number>)[t] ?? null;
}

// ── Types ─────────────────────────────────────────────────────────────

export const NotificationType = {
  Mention:         100000000,
  Assignment:      100000001,
  DueDateWarning:  100000002,
  StatusChange:    100000003,
  WatcherUpdate:   100000004,
  AutomationRule:  100000005,
  Comment:         100000006,
  SystemAlert:     100000007,
} as const;

export type NotificationTypeKey = keyof typeof NotificationType;

export type ActivityType =
  | 'taskMention'
  | 'mentionedInComment'
  | 'taskAssigned'
  | 'watcherUpdate'
  | 'taskDueSoon'
  | 'statusChanged';

export interface NotificationRecord {
  dagrun_pm_notificationid: string;
  dagrun_pm_name: string | null;
  dagrun_pm_recipientuserid: string;
  dagrun_pm_recipientusername: string | null;
  dagrun_pm_notificationtype: number;
  dagrun_pm_entitytype: string | null;
  dagrun_pm_entityid: string | null;
  dagrun_pm_entitytitle: string | null;
  dagrun_pm_projectid: string | null;
  dagrun_pm_projectname: string | null;
  dagrun_pm_actorid: string | null;
  dagrun_pm_actorname: string | null;
  dagrun_pm_body: string | null;
  dagrun_pm_deeplinkurl: string | null;
  dagrun_pm_isread: boolean;
  dagrun_pm_readon: string | null;
  dagrun_pm_teamsnotificationsent: boolean;
  dagrun_pm_teamsnotificationsenton: string | null;
  dagrun_pm_mentionid: string | null;
  dagrun_pm_expireson: string | null;
  createdon: string;
}

export interface MentionedUser {
  userId: string;
  userName: string;
}

// ── Config ────────────────────────────────────────────────────────────

const TEAMS_APP_ID =
  process.env.REACT_APP_TEAMS_APP_ID ?? 'cef22445-2648-4c8a-ad49-149f27c47502';
const APP_URL = 'https://pm.dagrun.io';
const TEAMS_ACTIVITY_SCOPE = ['https://graph.microsoft.com/TeamsActivity.Send'];
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

// ── Public: deep links ────────────────────────────────────────────────

/** The navigable kinds a notification deep link can target. App.tsx +
 *  ProjectDetail resolve these to the specific record's view. */
export type DeepLinkKind = 'task' | 'milestone' | 'rfp';

/** Map a stored entity-type label (dagrun_pm_entitytype / CommentEntityType
 *  key) to a deep-link kind. Anything not separately routable resolves as a
 *  task (the common mention/assignment target). */
export function entityTypeToDeepLinkKind(entityType: string | null | undefined): DeepLinkKind {
  switch ((entityType ?? '').toLowerCase()) {
    case 'milestone': return 'milestone';
    case 'rfp':       return 'rfp';
    default:          return 'task';
  }
}

/**
 * Build a Teams deep link that opens the app AT THE SPECIFIC RECORD (not the
 * dashboard). The target kind + id are encoded into BOTH:
 *   • the Teams subEntity context (`subEntityId = "{kind}:{id}"`) — used by
 *     native Teams clients that render the tab and pass page.subPageId, and
 *   • the webUrl fallback query (`?focusKind={kind}&focusId={id}`) — used on
 *     mobile / browsers where Teams launches the webUrl directly.
 * App.tsx parses either form (see the deep-link consumer there). A bare id
 * with no "kind:" prefix is still accepted there as a task, for back-compat
 * with links minted before this change.
 */
export function buildDeepLink(entityId: string, kind: DeepLinkKind = 'task'): string {
  const sub = `${kind}:${entityId}`;
  const context = encodeURIComponent(JSON.stringify({ subEntityId: sub }));
  const webUrl = encodeURIComponent(`${APP_URL}/my-dashboard?focusKind=${kind}&focusId=${entityId}`);
  return `https://teams.microsoft.com/l/entity/${TEAMS_APP_ID}/dashboard?context=${context}&webUrl=${webUrl}`;
}

// ── Internal helpers ──────────────────────────────────────────────────

async function getGraphActivityToken(pca: IPublicClientApplication): Promise<string> {
  return getToken(pca, TEAMS_ACTIVITY_SCOPE);
}

/**
 * ROOT CAUSE of the @mention-notification failure (verified against live
 * prod mention rows, 2026-07-04):
 *
 * The frontend was posting the activity notification with a **custom-text**
 * topic (`topic.source = 'text'`). Microsoft Graph only permits custom-text
 * topics with an **application** token — the MSAL NAA token here is
 * **delegated**, so every send was rejected:
 *   • cross-user recipient → 403 "…is not authorized to generate custom text
 *     notifications…" (proven on a NON-self recipient, so self-notification
 *     was never the real cause), and
 *   • self-recipient (same AAD OID) → 400 "recipient … cannot be the same as
 *     the sender".
 *
 * The delegated-compatible topic form is `source: 'entityUrl'` pointing at
 * the recipient's installation of this Teams app. This resolves that URL.
 * Requires the caller to be able to read the recipient's installedApps
 * (self, or app/admin-consented scope); when it can't (typical for a
 * cross-user delegated read, or the app not installed for the recipient),
 * we return null and DEFER delivery to the app-token dispatcher flow via
 * the durable dagrun_pm_notification row.
 */
async function resolveInstalledAppEntityUrl(
  pca: IPublicClientApplication,
  userId: string,
): Promise<string | null> {
  try {
    const data = await graphGet<{
      value: Array<{ id: string; teamsApp?: { id?: string; externalId?: string } }>;
    }>(pca, `/users/${userId}/teamwork/installedApps?$expand=teamsApp($select=id,externalId)&$select=id`);
    // The manifest's externalId is the app id in webApplicationInfo/manifest.
    const inst = data.value.find(a => a.teamsApp?.externalId === TEAMS_APP_ID);
    if (!inst) return null;
    return `https://graph.microsoft.com/v1.0/users/${userId}/teamwork/installedApps/${inst.id}`;
  } catch {
    return null;
  }
}

/**
 * Fetch the projectmember row for (userId, projectId) and bump its
 * unread_notification_count by `delta`, clamped to >= 0. Resilient to
 * the row not existing — silently returns in that case so notification
 * delivery is never blocked by a missing membership row.
 */
async function bumpUnreadCount(
  pca: IPublicClientApplication,
  userId: string,
  projectId: string,
  delta: number,
): Promise<void> {
  try {
    const rows = await dvList<{
      dagrun_pm_projectmemberid: string;
      dagrun_pm_unreadnotificationcount: number | null;
    }>(pca, 'dagrun_pm_projectmembers', {
      select: ['dagrun_pm_projectmemberid', 'dagrun_pm_unreadnotificationcount'],
      filter: `_dagrun_pm_projectid_value eq ${projectId} and dagrun_pm_userid eq '${userId}'`,
      top: 1,
    });
    const m = rows[0];
    if (!m) return;
    const next = Math.max(0, (m.dagrun_pm_unreadnotificationcount ?? 0) + delta);
    await dvUpdate(pca, 'dagrun_pm_projectmembers', m.dagrun_pm_projectmemberid, {
      dagrun_pm_unreadnotificationcount: next,
    });
  } catch {
    // Non-fatal — count drift recovers on the next "Mark all read".
  }
}

interface SendActivityArgs {
  pca: IPublicClientApplication;
  recipientUserId: string;
  actorId: string;
  activityType: ActivityType;
  topicValue: string;
  topicWebUrl: string;
  previewText: string; // Caller is responsible for the 150-char Graph limit.
  templateParams: Array<{ name: string; value: string }>;
}

/** Outcome of a best-effort frontend delivery attempt. */
type DeliveryOutcome = 'delivered' | 'deferred';

/**
 * Best-effort frontend Graph delivery of a Teams activity feed
 * notification.
 *
 * Uses an `entityUrl` topic (the recipient's installed-app URL) so a
 * DELEGATED MSAL NAA token is accepted — the previous `source: 'text'`
 * topic is application-permission-only and was rejected 403 for every
 * recipient (see resolveInstalledAppEntityUrl for the full diagnosis).
 *
 * Returns:
 *   • 'delivered' — Graph accepted the notification (arrives instantly).
 *   • 'deferred'  — we could not attempt from the frontend (self-recipient,
 *                   or the recipient's app install URL isn't readable with a
 *                   delegated token). NOT an error: the durable
 *                   dagrun_pm_notification row is left for the app-token
 *                   `dagrun_pm_NotificationDispatcher` flow to deliver.
 *
 * Throws only on an UNEXPECTED Graph error (a status other than the known
 * delegated-limitation ones), so genuine problems still surface.
 */
async function sendTeamsActivityNotification(args: SendActivityArgs): Promise<DeliveryOutcome> {
  // A delegated caller cannot send an activity notification to itself
  // (Graph 400 "recipient cannot be the same as the sender"). Defer to the
  // app-token flow, which has no sender identity and can self-notify.
  if (args.recipientUserId === args.actorId) return 'deferred';

  const entityUrl = await resolveInstalledAppEntityUrl(args.pca, args.recipientUserId);
  if (!entityUrl) return 'deferred';

  const token = await getGraphActivityToken(args.pca);
  const url = `https://graph.microsoft.com/v1.0/users/${args.recipientUserId}/teamwork/sendActivityNotification`;
  const body = {
    topic: {
      source: 'entityUrl',
      value: entityUrl,
    },
    activityType: args.activityType,
    previewText: { content: args.previewText },
    templateParameters: args.templateParams,
  };
  // Instrumentation (kept intentionally) — see the request just before it fires.
  console.log('MENTION: sending notification to userId:', args.recipientUserId, 'payload:', body);
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const text = await res.text().catch(() => '');
  // Instrumentation (kept intentionally) — see the Graph response status + body.
  console.log('MENTION: notification response status:', res.status, 'body:', text);
  if (res.ok) return 'delivered';
  // 400/403 here are the known delegated-token limitations (self-send /
  // permission scope). Treat as a deferral so the flow can still deliver;
  // surface anything else as a real error.
  if (res.status === 400 || res.status === 403) return 'deferred';
  throw new Error(`Graph sendActivityNotification ${res.status}: ${text.slice(0, 300)}`);
}

async function getCurrentActor(pca: IPublicClientApplication): Promise<{ id: string; name: string }> {
  const acct = await getAccount(pca);
  const id = acct?.localAccountId ?? acct?.homeAccountId?.split('.')[0] ?? '';
  const name = acct?.name ?? acct?.username ?? 'Someone';
  return { id, name };
}

// ── Public: send mention notification ─────────────────────────────────

interface SendMentionParams {
  commentId: string;
  commentBody: string;
  taskId: string;
  taskName: string;
  projectId: string;
  projectName: string;
  entityType: string;
  entityId: string;
  mentionedUsers: MentionedUser[];
}

/**
 * For each mentioned user:
 *   1. POST dagrun_pm_mention with the expanded denormalized fields.
 *   2. Build deep link.
 *   3. POST dagrun_pm_notification (type=Mention).
 *   4. POST Graph sendActivityNotification (mentionedInComment).
 *   5. PATCH the mention with notification_sent=true (or notification_error).
 *   6. PATCH the notification with teams_notification_sent=true.
 *   7. Bump the recipient's projectmember.unread_notification_count by 1.
 *
 * Steps are sequential per user — no fire-and-forget. A Graph failure
 * for one recipient does not abort the remaining recipients.
 */
export async function sendMentionNotification(
  pca: IPublicClientApplication,
  params: SendMentionParams,
): Promise<void> {
  const actor = await getCurrentActor(pca);
  const expiresOn = new Date(Date.now() + NINETY_DAYS_MS).toISOString();

  for (const user of params.mentionedUsers) {
    // (a) Mention row.
    const mentionId = await dvCreate(pca, 'dagrun_pm_mentions', {
      'dagrun_pm_commentid@odata.bind': `/dagrun_pm_comments(${params.commentId})`,
      dagrun_pm_mentioneduserid: user.userId,
      dagrun_pm_mentionedusername: user.userName,
      dagrun_pm_isateveryone: false,
      dagrun_pm_taskid: params.taskId,
      dagrun_pm_projectid: params.projectId,
      dagrun_pm_entitytype: params.entityType,
      dagrun_pm_entityid: params.entityId,
      dagrun_pm_notificationsent: false,
      dagrun_pm_name: `Mention: ${user.userName} on ${params.taskName}`.slice(0, 300),
    });

    // (b) Deep link.
    const deepLink = buildDeepLink(params.taskId, 'task');

    // (c) Notification row.
    const body = `${actor.name} mentioned you on "${params.taskName}"`;
    const notifId = await dvCreate(pca, 'dagrun_pm_notifications', {
      dagrun_pm_recipientuserid: user.userId,
      dagrun_pm_recipientusername: user.userName,
      dagrun_pm_notificationtype: NotificationType.Mention,
      dagrun_pm_entitytype: params.entityType,
      dagrun_pm_entityid: params.entityId,
      dagrun_pm_entitytitle: params.taskName,
      dagrun_pm_projectid: params.projectId,
      dagrun_pm_projectname: params.projectName,
      dagrun_pm_actorid: actor.id,
      dagrun_pm_actorname: actor.name,
      dagrun_pm_body: body.slice(0, 500),
      dagrun_pm_deeplinkurl: deepLink.slice(0, 1000),
      dagrun_pm_isread: false,
      dagrun_pm_teamsnotificationsent: false,
      dagrun_pm_mentionid: mentionId,
      dagrun_pm_expireson: expiresOn,
      dagrun_pm_name: body.slice(0, 300),
    });

    // (d) Graph notification + (e/f) flag updates.
    const previewText = params.commentBody.slice(0, 150);
    let outcome: DeliveryOutcome = 'deferred';
    let graphError: string | null = null;
    try {
      outcome = await sendTeamsActivityNotification({
        pca,
        recipientUserId: user.userId,
        actorId: actor.id,
        activityType: 'taskMention',
        topicValue: params.taskName,
        topicWebUrl: deepLink,
        previewText,
        templateParams: [
          { name: 'taskName', value: params.taskName },
          { name: 'actor', value: actor.name },
        ],
      });
    } catch (e) {
      graphError = e instanceof Error ? e.message : String(e);
    }

    // Mention row / notification row flag updates:
    //   • delivered → mark both rows sent.
    //   • deferred  → leave teamsnotificationsent=false so the app-token
    //                 dispatcher flow delivers it; no error is a normal state.
    //   • error     → record the unexpected Graph error for diagnosis.
    if (graphError) {
      await dvUpdate(pca, 'dagrun_pm_mentions', mentionId, {
        dagrun_pm_notificationerror: graphError.slice(0, 500),
      });
    } else if (outcome === 'delivered') {
      await dvUpdate(pca, 'dagrun_pm_mentions', mentionId, {
        dagrun_pm_notificationsent: true,
        dagrun_pm_notificationsenton: new Date().toISOString(),
      });
      await dvUpdate(pca, 'dagrun_pm_notifications', notifId, {
        dagrun_pm_teamsnotificationsent: true,
        dagrun_pm_teamsnotificationsenton: new Date().toISOString(),
      });
    }

    // (g) Bump unread count for recipient.
    await bumpUnreadCount(pca, user.userId, params.projectId, 1);
  }
}

// ── Public: send watcher / status / assignment notification ──────────

interface SendWatcherParams {
  entityType: string;
  entityId: string;
  entityTitle: string;
  projectId: string;
  projectName: string;
  changeDescription: string;
  actorId: string;
  actorName: string;
  notificationType: 'WatcherUpdate' | 'StatusChange' | 'Assignment';
  activityType: 'watcherUpdate' | 'statusChanged' | 'taskAssigned';
  /**
   * If provided, skip the watcher query and notify these recipients
   * directly. Used for assignment notifications where the recipient is
   * the new assignee, not a generic watcher.
   */
  recipientOverride?: MentionedUser[];
}

interface WatcherRow {
  dagrun_pm_watcherid: string;
  dagrun_pm_userid: string;
  dagrun_pm_entitytype: number | null;
  dagrun_pm_entityid: string | null;
}

/**
 * Sends an activity notification to all watchers of an entity (or to
 * an explicit recipientOverride list when provided). The actor is
 * filtered out so the person making the change isn't notified about
 * their own change.
 */
export async function sendWatcherNotification(
  pca: IPublicClientApplication,
  params: SendWatcherParams,
): Promise<void> {
  const expiresOn = new Date(Date.now() + NINETY_DAYS_MS).toISOString();
  const notifTypeValue = NotificationType[params.notificationType];

  // Resolve recipients.
  let recipients: MentionedUser[];
  if (params.recipientOverride && params.recipientOverride.length > 0) {
    recipients = params.recipientOverride.filter(r => r.userId !== params.actorId);
  } else {
    // dagrun_pm_watcher.dagrun_pm_entitytype is a Picklist — query by
    // its integer value, not the string name from params.entityType.
    const entityTypeInt = entityTypeToInt(params.entityType);
    if (entityTypeInt == null) return;
    const rows = await dvList<WatcherRow>(pca, 'dagrun_pm_watchers', {
      select: ['dagrun_pm_watcherid', 'dagrun_pm_userid',
               'dagrun_pm_entitytype', 'dagrun_pm_entityid'],
      filter: `dagrun_pm_entitytype eq ${entityTypeInt} and dagrun_pm_entityid eq '${params.entityId}'`,
      top: 500,
    });
    recipients = rows
      .filter(w => w.dagrun_pm_userid && w.dagrun_pm_userid !== params.actorId)
      .map(w => ({ userId: w.dagrun_pm_userid, userName: 'Watcher' }));
  }

  if (recipients.length === 0) return;

  const deepLink = buildDeepLink(params.entityId, entityTypeToDeepLinkKind(params.entityType));
  const previewText = params.changeDescription.slice(0, 150);

  for (const user of recipients) {
    const body = params.changeDescription;
    const notifId = await dvCreate(pca, 'dagrun_pm_notifications', {
      dagrun_pm_recipientuserid: user.userId,
      dagrun_pm_recipientusername: user.userName,
      dagrun_pm_notificationtype: notifTypeValue,
      dagrun_pm_entitytype: params.entityType,
      dagrun_pm_entityid: params.entityId,
      dagrun_pm_entitytitle: params.entityTitle,
      dagrun_pm_projectid: params.projectId,
      dagrun_pm_projectname: params.projectName,
      dagrun_pm_actorid: params.actorId,
      dagrun_pm_actorname: params.actorName,
      dagrun_pm_body: body.slice(0, 500),
      dagrun_pm_deeplinkurl: deepLink.slice(0, 1000),
      dagrun_pm_isread: false,
      dagrun_pm_teamsnotificationsent: false,
      dagrun_pm_expireson: expiresOn,
      dagrun_pm_name: body.slice(0, 300),
    });

    let outcome: DeliveryOutcome = 'deferred';
    try {
      outcome = await sendTeamsActivityNotification({
        pca,
        recipientUserId: user.userId,
        actorId: params.actorId,
        activityType: params.activityType,
        topicValue: params.entityTitle,
        topicWebUrl: deepLink,
        previewText,
        templateParams: [
          { name: 'taskName', value: params.entityTitle },
          { name: 'actor', value: params.actorName },
        ],
      });
    } catch {
      // Unexpected Graph error — leave teamsnotificationsent=false so the
      // dispatcher flow retries with an app token.
      outcome = 'deferred';
    }

    // delivered → mark sent; deferred → leave for the app-token flow.
    if (outcome === 'delivered') {
      await dvUpdate(pca, 'dagrun_pm_notifications', notifId, {
        dagrun_pm_teamsnotificationsent: true,
        dagrun_pm_teamsnotificationsenton: new Date().toISOString(),
      });
    }

    await bumpUnreadCount(pca, user.userId, params.projectId, 1);
  }
}

// ── Public: read-state mutation ───────────────────────────────────────

/**
 * Marks a single notification read and decrements the recipient's
 * projectmember.unread_notification_count by 1 (clamped to >= 0).
 */
export async function markNotificationRead(
  pca: IPublicClientApplication,
  notificationId: string,
  userId: string,
  projectId: string,
): Promise<void> {
  await dvUpdate(pca, 'dagrun_pm_notifications', notificationId, {
    dagrun_pm_isread: true,
    dagrun_pm_readon: new Date().toISOString(),
  });
  await bumpUnreadCount(pca, userId, projectId, -1);
}

/**
 * Marks all unread notifications for a user as read. When projectId is
 * null, sweeps across ALL projects and resets every projectmember
 * unread count for this user to 0 (used by the global Notification
 * Center "Mark all read" action). When projectId is provided, scoped
 * to that one project only.
 */
export async function markAllRead(
  pca: IPublicClientApplication,
  userId: string,
  projectId: string | null,
): Promise<void> {
  // 1. Fetch unread notifications.
  const filter = projectId
    ? `dagrun_pm_recipientuserid eq '${userId}' and dagrun_pm_projectid eq '${projectId}' and dagrun_pm_isread eq false`
    : `dagrun_pm_recipientuserid eq '${userId}' and dagrun_pm_isread eq false`;
  const rows = await dvList<{ dagrun_pm_notificationid: string }>(pca, 'dagrun_pm_notifications', {
    select: ['dagrun_pm_notificationid'],
    filter,
    top: 5000,
  });

  // 2. Mark each read. Sequential PATCHes — Dataverse $batch is heavier
  //    plumbing and the notification volume per user is bounded by the
  //    90-day expires_on plus typical mention frequency, so a few
  //    hundred PATCHes is the realistic ceiling.
  const readOn = new Date().toISOString();
  for (const r of rows) {
    try {
      await dvUpdate(pca, 'dagrun_pm_notifications', r.dagrun_pm_notificationid, {
        dagrun_pm_isread: true,
        dagrun_pm_readon: readOn,
      });
    } catch {
      // Skip on error — the next "Mark all" attempt will pick it up.
    }
  }

  // 3. Reset projectmember unread counts.
  const memberFilter = projectId
    ? `_dagrun_pm_projectid_value eq ${projectId} and dagrun_pm_userid eq '${userId}'`
    : `dagrun_pm_userid eq '${userId}'`;
  const members = await dvList<{ dagrun_pm_projectmemberid: string }>(pca, 'dagrun_pm_projectmembers', {
    select: ['dagrun_pm_projectmemberid'],
    filter: memberFilter,
    top: 500,
  });
  for (const m of members) {
    try {
      await dvUpdate(pca, 'dagrun_pm_projectmembers', m.dagrun_pm_projectmemberid, {
        dagrun_pm_unreadnotificationcount: 0,
      });
    } catch {
      // Skip — count drift recovers on next successful Mark all read.
    }
  }
}

// ── Public: list / count helpers (used by Notification Center + bell) ─

/**
 * List notifications for a user across all projects, ordered by createdon
 * descending. Used by the Notification Center panel.
 */
export async function listUserNotifications(
  pca: IPublicClientApplication,
  userId: string,
  unreadOnly: boolean,
  topN = 200,
): Promise<NotificationRecord[]> {
  const filter = unreadOnly
    ? `dagrun_pm_recipientuserid eq '${userId}' and dagrun_pm_isread eq false`
    : `dagrun_pm_recipientuserid eq '${userId}'`;
  return dvList<NotificationRecord>(pca, 'dagrun_pm_notifications', {
    select: [
      'dagrun_pm_notificationid', 'dagrun_pm_name', 'dagrun_pm_recipientuserid',
      'dagrun_pm_recipientusername', 'dagrun_pm_notificationtype',
      'dagrun_pm_entitytype', 'dagrun_pm_entityid', 'dagrun_pm_entitytitle',
      'dagrun_pm_projectid', 'dagrun_pm_projectname',
      'dagrun_pm_actorid', 'dagrun_pm_actorname',
      'dagrun_pm_body', 'dagrun_pm_deeplinkurl',
      'dagrun_pm_isread', 'dagrun_pm_readon',
      'dagrun_pm_teamsnotificationsent', 'dagrun_pm_teamsnotificationsenton',
      'dagrun_pm_mentionid', 'dagrun_pm_expireson',
      'createdon',
    ],
    filter,
    orderby: 'createdon desc',
    top: topN,
  });
}

/**
 * Sum unread_notification_count across all projectmember rows for a
 * user. Cheap — small number of project memberships per user — and
 * avoids the live-aggregate query the spec explicitly forbids on the
 * notification table.
 */
export async function getTotalUnreadCount(
  pca: IPublicClientApplication,
  userId: string,
): Promise<number> {
  const rows = await dvList<{ dagrun_pm_unreadnotificationcount: number | null }>(
    pca, 'dagrun_pm_projectmembers', {
      select: ['dagrun_pm_unreadnotificationcount'],
      filter: `dagrun_pm_userid eq '${userId}'`,
      top: 500,
    },
  );
  return rows.reduce((sum, r) => sum + (r.dagrun_pm_unreadnotificationcount ?? 0), 0);
}

/**
 * Returns watcher rows for the current user. The dagrun_pm_watcher table
 * stores entity_type as a Picklist (integer) — the helper translates
 * back to the string keys the rest of the app uses.
 */
export interface WatchedItem {
  watcherId: string;
  entityType: EntityTypeName | 'Unknown';
  entityTypeInt: number;
  entityId: string;
}
/**
 * Look up the current user's watcher row for one specific entity.
 * Returns the watcher GUID when present (for delete) or null when the
 * user is not watching this entity. Used by the Watch toggle in the
 * task detail drawer to render its initial state.
 */
export async function getMyWatcher(
  pca: IPublicClientApplication,
  entityType: string,
  entityId: string,
  userId: string,
): Promise<string | null> {
  const entityTypeInt = entityTypeToInt(entityType);
  if (entityTypeInt == null) return null;
  const rows = await dvList<{ dagrun_pm_watcherid: string }>(pca, 'dagrun_pm_watchers', {
    select: ['dagrun_pm_watcherid'],
    filter: `dagrun_pm_userid eq '${userId}' and dagrun_pm_entitytype eq ${entityTypeInt} and dagrun_pm_entityid eq '${entityId}'`,
    top: 1,
  });
  return rows[0]?.dagrun_pm_watcherid ?? null;
}

/**
 * Add the current user as a watcher for an entity. Returns the new
 * watcher GUID so the caller can store it for the eventual unwatch.
 * Idempotent at the UI layer: callers should check getMyWatcher first
 * to avoid creating duplicates if a record already exists.
 */
export async function addWatcher(
  pca: IPublicClientApplication,
  entityType: string,
  entityId: string,
  userId: string,
  userName: string,
): Promise<string> {
  const entityTypeInt = entityTypeToInt(entityType);
  if (entityTypeInt == null) throw new Error(`addWatcher: unknown entityType "${entityType}"`);
  // NOTE: dagrun_pm_watcher has NO dagrun_pm_username column (verified via
  // EntityDefinitions on prod). Writing it caused the create to fail — the
  // watch toggle's "404"/error. The watcher's display name lives in
  // dagrun_pm_name; userName is folded into that label.
  return dvCreate(pca, 'dagrun_pm_watchers', {
    dagrun_pm_userid: userId,
    dagrun_pm_entitytype: entityTypeInt,
    dagrun_pm_entityid: entityId,
    dagrun_pm_name: `${userName} watching ${entityType} ${entityId}`.slice(0, 300),
  });
}

/**
 * Remove a watcher row by id (returned by getMyWatcher / addWatcher).
 */
export async function removeWatcher(
  pca: IPublicClientApplication,
  watcherId: string,
): Promise<void> {
  await dvDelete(pca, 'dagrun_pm_watchers', watcherId);
}

export async function listMyWatchedItems(
  pca: IPublicClientApplication,
  userId: string,
): Promise<WatchedItem[]> {
  const rows = await dvList<{
    dagrun_pm_watcherid: string;
    dagrun_pm_entitytype: number | null;
    dagrun_pm_entityid: string | null;
  }>(pca, 'dagrun_pm_watchers', {
    select: ['dagrun_pm_watcherid', 'dagrun_pm_entitytype', 'dagrun_pm_entityid'],
    filter: `dagrun_pm_userid eq '${userId}'`,
    top: 500,
  });
  const intToName: Record<number, EntityTypeName> = {};
  (Object.keys(WatcherEntityType) as EntityTypeName[]).forEach(k => {
    intToName[WatcherEntityType[k]] = k;
  });
  return rows
    .filter(r => r.dagrun_pm_entityid && r.dagrun_pm_entitytype != null)
    .map(r => ({
      watcherId: r.dagrun_pm_watcherid,
      entityType: intToName[r.dagrun_pm_entitytype as number] ?? 'Unknown',
      entityTypeInt: r.dagrun_pm_entitytype as number,
      entityId: r.dagrun_pm_entityid as string,
    }));
}

/**
 * Returns mention rows for a user, joined client-side with their parent
 * comment so the Mentions section can render the FULL comment body and
 * the linked notification's read state in one pass. Task names are
 * resolved by fetching the referenced dagrun_pm_task records so the
 * Mentions section can show "{author} mentioned you on {task name}"
 * rather than a bare GUID.
 */
export interface MentionFeedItem {
  mentionId: string;
  commentId: string;
  commentBody: string;
  authorName: string | null;
  taskId: string | null;
  taskName: string | null;
  projectId: string | null;
  entityType: string | null;
  entityId: string | null;
  entityName: string | null;
  createdOn: string;
  // Linked notification (if any) — drives the unread dot.
  notificationId: string | null;
  isRead: boolean;
}

export async function listMyMentions(
  pca: IPublicClientApplication,
  userId: string,
  topN = 100,
): Promise<MentionFeedItem[]> {
  const mentions = await dvList<{
    dagrun_pm_mentionid: string;
    dagrun_pm_commentid: string | null;
    _dagrun_pm_commentid_value: string;
    dagrun_pm_taskid: string | null;
    dagrun_pm_projectid: string | null;
    dagrun_pm_entitytype: string | null;
    dagrun_pm_entityid: string | null;
    createdon: string;
  }>(pca, 'dagrun_pm_mentions', {
    select: ['dagrun_pm_mentionid', '_dagrun_pm_commentid_value',
             'dagrun_pm_taskid', 'dagrun_pm_projectid',
             'dagrun_pm_entitytype', 'dagrun_pm_entityid', 'createdon'],
    filter: `dagrun_pm_mentioneduserid eq '${userId}'`,
    orderby: 'createdon desc',
    top: topN,
  });
  if (mentions.length === 0) return [];

  // Hydrate comments + notifications + task names in parallel. Task
  // names come from dagrun_pm_tasks via dagrun_pm_taskid (a string GUID
  // stored on the mention row, not a lookup) so we filter with
  // `dagrun_pm_taskid eq <guid>` rather than expanding a navigation.
  const commentIds = Array.from(new Set(
    mentions.map(m => m._dagrun_pm_commentid_value).filter((x): x is string => !!x),
  ));
  const mentionIds = mentions.map(m => m.dagrun_pm_mentionid);
  const taskIds = Array.from(new Set(
    mentions.map(m => m.dagrun_pm_taskid).filter((x): x is string => !!x),
  ));

  const [comments, notifs, tasks] = await Promise.all([
    commentIds.length > 0 ? dvList<{
      dagrun_pm_commentid: string;
      dagrun_pm_body: string | null;
      dagrun_pm_authorname: string | null;
    }>(pca, 'dagrun_pm_comments', {
      select: ['dagrun_pm_commentid', 'dagrun_pm_body', 'dagrun_pm_authorname'],
      filter: commentIds.map(id => `dagrun_pm_commentid eq ${id}`).join(' or '),
      top: commentIds.length,
    }).catch(() => []) : Promise.resolve([]),
    dvList<{
      dagrun_pm_notificationid: string;
      dagrun_pm_mentionid: string | null;
      dagrun_pm_isread: boolean;
    }>(pca, 'dagrun_pm_notifications', {
      select: ['dagrun_pm_notificationid', 'dagrun_pm_mentionid', 'dagrun_pm_isread'],
      filter: `dagrun_pm_recipientuserid eq '${userId}' and (${mentionIds.map(id => `dagrun_pm_mentionid eq '${id}'`).join(' or ')})`,
      top: mentionIds.length,
    }).catch(() => []),
    taskIds.length > 0 ? dvList<{
      dagrun_pm_taskid: string;
      dagrun_pm_name: string | null;
    }>(pca, 'dagrun_pm_tasks', {
      select: ['dagrun_pm_taskid', 'dagrun_pm_name'],
      filter: taskIds.map(id => `dagrun_pm_taskid eq ${id}`).join(' or '),
      top: taskIds.length,
    }).catch(() => []) : Promise.resolve([]),
  ]);

  const commentById = new Map(comments.map(c => [c.dagrun_pm_commentid, c]));
  const notifByMentionId = new Map(
    notifs.filter(n => n.dagrun_pm_mentionid).map(n => [n.dagrun_pm_mentionid as string, n]),
  );
  const taskNameById = new Map(tasks.map(t => [t.dagrun_pm_taskid, t.dagrun_pm_name]));

  return mentions.map<MentionFeedItem>(m => {
    const c = m._dagrun_pm_commentid_value ? commentById.get(m._dagrun_pm_commentid_value) : undefined;
    const n = notifByMentionId.get(m.dagrun_pm_mentionid);
    const taskName = m.dagrun_pm_taskid ? (taskNameById.get(m.dagrun_pm_taskid) ?? null) : null;
    return {
      mentionId: m.dagrun_pm_mentionid,
      commentId: m._dagrun_pm_commentid_value ?? '',
      commentBody: c?.dagrun_pm_body ?? '',
      authorName: c?.dagrun_pm_authorname ?? null,
      taskId: m.dagrun_pm_taskid,
      taskName,
      projectId: m.dagrun_pm_projectid,
      entityType: m.dagrun_pm_entitytype,
      entityId: m.dagrun_pm_entityid,
      entityName: taskName,
      createdOn: m.createdon,
      notificationId: n?.dagrun_pm_notificationid ?? null,
      isRead: n ? n.dagrun_pm_isread : true,
    };
  });
}
